import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Cloud, Database, Loader2, RefreshCw, ShieldCheck, ShieldAlert, Trash2 } from "lucide-react";
import platformApi from "@/lib/platformApi";
import { formatApiErrorDetail } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const GOLD_INK = "var(--brand-accent-ink)";
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB",
  { day: "2-digit", month: "short", year: "numeric" }) : "—");

export default function CloudConnectionsPanel({ isRoot }) {
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revoking, setRevoking] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const { data } = await platformApi.get("/cloud-connections");
      setData(data);
    } finally {
      setRefreshing(false);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const revoke = async () => {
    setRevoking(true);
    try {
      await platformApi.delete(`/cloud-connections/${revokeTarget.tenant_id}/${revokeTarget.id}`);
      toast.success(`Revoked ${revokeTarget.provider_label} for ${revokeTarget.tenant_id}`);
      setRevokeTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setRevoking(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="cloud-connections-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-[13px] font-bold uppercase tracking-[0.14em] text-[#1E293B] flex items-center gap-2">
          <Cloud className="h-4 w-4" style={{ color: GOLD_INK }} /> Cloud connections
          <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 border bg-slate-100 border-slate-300 text-slate-600">
            {isRoot ? "root can revoke — credentials never shown" : "view only — credentials never shown"}
          </span>
        </h2>
        <button onClick={load} disabled={refreshing} data-testid="cloud-refresh"
          className="text-[10px] font-mono border border-slate-300 px-2 py-1.5 hover:bg-slate-50 disabled:opacity-50 flex items-center gap-1">
          {refreshing ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} refresh
        </button>
      </div>

      {!data ? <Skeleton className="h-48 w-full rounded-none" /> : data.connections.length === 0 ? (
        <div className="bg-white border border-slate-200 px-5 py-10 text-center" data-testid="cloud-empty">
          <Database className="h-8 w-8 text-slate-300 mx-auto mb-3" />
          <p className="text-[13px] text-slate-500">No tenant has linked an enterprise database yet.</p>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-4 text-[11px] font-mono text-slate-500">
            <span data-testid="cloud-total">{data.count} connection{data.count === 1 ? "" : "s"}</span>
            <span className="text-teal-700">{data.sovereign_count} in sovereign region</span>
          </div>
          <div className="bg-white border border-slate-200 overflow-x-auto">
            <table className="w-full text-[12px]" data-testid="cloud-connections-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-200 bg-slate-50">
                  {["Tenant", "Provider", "Region", "Residency", "Host", "Reachability", "Configured", ...(isRoot ? ["Actions"] : [])].map((h, i) => (
                    <th key={i} className="px-4 py-2.5 font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.connections.map((c) => (
                  <tr key={c.id} data-testid={`cloud-row-${c.id}`}>
                    <td className="px-4 py-2.5">
                      <p className="font-semibold text-[#1E293B]">{c.org_name}</p>
                      <p className="font-mono text-[10px] text-slate-400">{c.tenant_id}</p>
                    </td>
                    <td className="px-4 py-2.5 text-slate-700">{c.provider_label}</td>
                    <td className="px-4 py-2.5 text-slate-500">{c.region_label}</td>
                    <td className="px-4 py-2.5">
                      {c.sovereign ? (
                        <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 border bg-teal-50 border-teal-200 text-teal-700 flex items-center gap-1 w-fit">
                          <ShieldCheck className="h-3 w-3" /> QFC DPR
                        </span>
                      ) : (
                        <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 border bg-amber-50 border-amber-200 text-amber-700 flex items-center gap-1 w-fit">
                          <ShieldAlert className="h-3 w-3" /> cross-border
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-[10px] text-slate-400">{c.host_masked}</td>
                    <td className="px-4 py-2.5">
                      {c.last_test ? (
                        <span className={`text-[10px] font-mono ${c.last_test.reachable ? "text-teal-700" : "text-slate-400"}`}>
                          {c.last_test.reachable ? "reachable" : "unreachable"} · {fmt(c.last_test.tested_at)}
                        </span>
                      ) : <span className="text-[10px] text-slate-300">not tested</span>}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-[10px] text-slate-400">{fmt(c.created_at)}</td>
                    {isRoot && (
                      <td className="px-4 py-2.5">
                        <button data-testid={`cloud-revoke-${c.id}`} onClick={() => setRevokeTarget(c)}
                          className="text-[10px] font-mono uppercase border border-slate-300 px-2 py-1 hover:bg-red-50 hover:text-red-600 flex items-center gap-1">
                          <Trash2 className="h-3 w-3" /> revoke
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <Dialog open={!!revokeTarget} onOpenChange={(o) => { if (!o) setRevokeTarget(null); }}>
        <DialogContent className="rounded-none sm:max-w-md" data-testid="cloud-revoke-dialog">
          <DialogHeader>
            <DialogTitle className="text-[14px] font-bold text-[#1E293B]">Revoke cloud connection</DialogTitle>
          </DialogHeader>
          <p className="text-[12px] text-slate-500">
            Remove the <span className="font-semibold">{revokeTarget?.provider_label}</span> connection for{" "}
            <span className="font-mono">{revokeTarget?.tenant_id}</span>? The tenant will need to re-link its
            database. This is recorded in the Observatory feed.
          </p>
          <DialogFooter>
            <Button data-testid="cloud-revoke-confirm" onClick={revoke} disabled={revoking}
              className="rounded-none bg-red-600 hover:bg-red-700 text-white">
              {revoking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Revoke connection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
